import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LIMIT_AUDITS } from '../../ChangeLog/constants';

type PaginatedVariables = { paginationOptions: { limit: number; offset: number } };

type UsePaginationProps<T, V extends PaginatedVariables> = {
  filteredChangeLogList: T[];
  total: number;
  setQueryVariables: Dispatch<SetStateAction<V>>;
  setDesiredItemsToShow: (count: number) => void;
  desiredItemsToShow: number;
  hasNextPage: boolean;
};

type UsePaginationResult<T> = {
  filteredLogsToShow: T[];
  hasMore: boolean;
  onNext: () => void;
};

export const usePagination = <T, V extends PaginatedVariables>({
  filteredChangeLogList,
  total,
  setQueryVariables,
  setDesiredItemsToShow,
  desiredItemsToShow,
  hasNextPage,
}: UsePaginationProps<T, V>): UsePaginationResult<T> => {
  const isInitialMount = useRef(true);
  const [isLoading, setIsLoading] = useState(false);

  const filteredChangeLogListCount = filteredChangeLogList.length;

  const hasMore = hasNextPage;
  const getMoreLogs = useCallback(() => {
    if (
      !isLoading &&
      hasMore &&
      filteredChangeLogListCount < desiredItemsToShow + 1 &&
      total > filteredChangeLogListCount
    ) {
      setIsLoading(true);
      setQueryVariables((prevVariables) => ({
        ...prevVariables,
        paginationOptions: {
          limit: prevVariables.paginationOptions.limit + LIMIT_AUDITS,
          offset: 0,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredItemsToShow]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      return;
    }

    getMoreLogs();
  }, [desiredItemsToShow, hasMore, getMoreLogs]);

  const onNext = useCallback((): void => {
    setDesiredItemsToShow(desiredItemsToShow + LIMIT_AUDITS);
  }, [desiredItemsToShow, setDesiredItemsToShow]);

  return { filteredLogsToShow: filteredChangeLogList, hasMore, onNext };
};
